import { Link } from "react-router-dom";
import { Phone, MapPin, Clock, Instagram, Facebook, MessageCircle } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { useCategories } from "@/lib/queries";
import { whatsappLink, telLink, mapsLink } from "@/lib/contact";

const REGIONAL_LINKS = [
  { label: "PPF Kaplama Kocaeli", to: "/hizmetler/ppf-kaplama-kocaeli", testid: "footer-ppf-kocaeli" },
  { label: "PPF Kaplama İzmit", to: "/ppf-kaplama-izmit", testid: "footer-ppf-izmit" },
  { label: "Seramik Kaplama Kocaeli", to: "/hizmetler/seramik-kaplama-kocaeli", testid: "footer-seramik-kocaeli" },
  { label: "Seramik Kaplama İzmit", to: "/seramik-kaplama-izmit", testid: "footer-seramik-izmit" },
  { label: "Cam Filmi İzmit", to: "/cam-filmi-izmit", testid: "footer-cam-izmit" },
  { label: "Pasta Cila İzmit", to: "/pasta-cila-izmit", testid: "footer-pasta-izmit" },
  { label: "Detaylı Temizlik İzmit", to: "/detayli-arac-temizligi-izmit", testid: "footer-detayli-izmit" },
  { label: "Far PPF İzmit", to: "/far-ppf-izmit", testid: "footer-farppf-izmit" },
];

export default function Footer() {
  const settings = useSettings();
  const { data: categories = [] } = useCategories();
  const year = new Date().getFullYear();

  return (
    <footer data-testid="site-footer" className="relative border-t border-white/10 bg-neutral-950 pb-28 pt-16 lg:pb-16">
      <div className="container-x grid gap-12 sm:grid-cols-2 lg:grid-cols-[1.3fr_1fr_1fr_1fr_1.1fr]">
        <div>
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="Meguiar's İzmit" className="h-11 w-11 rounded-md object-contain" />
            <span className="font-display text-lg font-semibold text-white">Meguiar's İzmit</span>
          </Link>
          <p className="mt-5 max-w-sm text-sm leading-relaxed text-neutral-400">
            Kocaeli'nin ilk ve lider Meguiar's araç bakım merkezi. Aracınız yalnızca temizlenmez; korunur, yenilenir ve değer kazanır.
          </p>
          <div className="mt-6 flex gap-3">
            <a href={settings?.instagram || "#"} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 text-neutral-300 hover:border-gold/40 hover:text-gold transition-colors">
              <Instagram className="h-4 w-4" />
            </a>
            <a href={settings?.facebook || "#"} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 text-neutral-300 hover:border-gold/40 hover:text-gold transition-colors">
              <Facebook className="h-4 w-4" />
            </a>
            <a href={whatsappLink(settings)} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 text-neutral-300 hover:border-gold/40 hover:text-gold transition-colors">
              <MessageCircle className="h-4 w-4" />
            </a>
          </div>
        </div>

        <div>
          <h4 className="font-display text-sm font-semibold uppercase tracking-wider text-white">Hizmetler</h4>
          <ul className="mt-5 space-y-3 text-sm">
            {categories.slice(0, 6).map((c) => (
              <li key={c.slug}>
                <Link to={`/hizmetler/kategori/${c.slug}`} className="text-neutral-400 hover:text-gold transition-colors">{c.name}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-display text-sm font-semibold uppercase tracking-wider text-white">Bölgesel Hizmetler</h4>
          <ul className="mt-5 space-y-3 text-sm">
            {REGIONAL_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} data-testid={l.testid} className="text-neutral-400 hover:text-gold transition-colors">{l.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-display text-sm font-semibold uppercase tracking-wider text-white">Kurumsal</h4>
          <ul className="mt-5 space-y-3 text-sm">
            <li><Link to="/hizmetler" className="text-neutral-400 hover:text-gold transition-colors">Tüm Hizmetler</Link></li>
            <li><Link to="/uygulamalar" className="text-neutral-400 hover:text-gold transition-colors">Uygulamalar</Link></li>
            <li><Link to="/blog" className="text-neutral-400 hover:text-gold transition-colors">Araç Bakım Rehberi</Link></li>
            <li><Link to="/randevu" className="text-neutral-400 hover:text-gold transition-colors">Randevu Al</Link></li>
            <li><Link to="/iletisim" className="text-neutral-400 hover:text-gold transition-colors">İletişim</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-display text-sm font-semibold uppercase tracking-wider text-white">İletişim</h4>
          <ul className="mt-5 space-y-4 text-sm">
            <li>
              <a href={telLink(settings)} className="flex items-start gap-3 text-neutral-400 hover:text-gold transition-colors">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {settings?.phone}
              </a>
            </li>
            <li>
              <a href={mapsLink(settings)} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 text-neutral-400 hover:text-gold transition-colors">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {settings?.address}
              </a>
            </li>
            <li className="flex items-start gap-3 text-neutral-400">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {settings?.working_hours}
            </li>
          </ul>
        </div>
      </div>

      <div className="container-x mt-12 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-6 text-xs text-neutral-500 sm:flex-row">
        <p>© {year} Meguiar's İzmit. Tüm hakları saklıdır.</p>
        <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
          <Link to="/gizlilik-politikasi" className="hover:text-gold transition-colors" data-testid="footer-privacy">Gizlilik Politikası</Link>
          <Link to="/kvkk-aydinlatma-metni" className="hover:text-gold transition-colors" data-testid="footer-kvkk">KVKK Aydınlatma</Link>
          <Link to="/cerez-politikasi" className="hover:text-gold transition-colors" data-testid="footer-cookies">Çerez Politikası</Link>
        </nav>
      </div>
    </footer>
  );
}
